import React, { useEffect, useRef } from 'react';
import { useStore, StoreTypes } from 'context';
import { EventBus } from 'events/EventBus';
import { UserEvent } from 'events/EventTypes';
import { AnnotationType } from 'constants/annotationTypes';
import { Roles } from 'constants/role';
import { usePullAnnotations, useRefreshReader } from 'customHooks/reader';
import { usePreparationSnapshot } from 'customHooks/preparation';

import { ReaderEvent, ReaderToolsEvent } from 'events/EventTypes';
import { useReadAnnotations } from 'customHooks/db';
import { usePreparation } from 'customHooks/preparation';
import { useUpdateDisplayLanguage, useUpdateUserSettings } from 'customHooks/userSetting';
import { UserSettingsAPI } from 'api/UserSettingsAPI';
import { useFirebase } from 'customHooks/firebase';
import * as types from 'constants/actionTypes';

const EDITOR_DOMAIN = 'editor.oneclass.com.tw';

const fetchIsValidDomain = async (parentDomain) => {
    if (!parentDomain) return false;
    try {
        const checkDomain = await fetch(
            `${process.env.REACT_APP_API_DOMAIN}/checkValidDomain`,
            {
                method: 'POST',
                body: JSON.stringify({ domain: parentDomain }),
                headers: { 'Content-Type': 'application/json' },
            }
        ).then((res) => res.json());
        return !!(checkDomain && checkDomain.status === 'success' && checkDomain.data.isValidDomain);
    } catch (err) {
        console.log('checkValidDomain error', err);
        return false;
    }
};

export const useAnnotationChecker = (props) => {
    const router = useStore(StoreTypes.router);
    const { bookId, paramsRole, paramsActivityId, paramsTeacherId, paramsInteractiveObjectId, roomId, studentId, timeSpanId, code, userName, id, remote, remoteId } = props
    const [{ annotationId, annotationType }] = useStore(StoreTypes.annotation);
    const [{ isLiveStreamMasterPanelShow, areaZoomInteractiveObjects, submenuInfo, isDoublePageMode, pageIndex, readerToolDirection, indicatorInfo: { isActive: isIndicatorActive, message: indicatorMessage } }, readerDispatch] = useStore(StoreTypes.reader);

    const [{ books }] = useStore(StoreTypes.books);
    const [{ role, token, userId, name }, userDispatch] = useStore(StoreTypes.user);
    const [, courseDispatch] = useStore(StoreTypes.course);

    const { preparationSnapshot } = usePreparationSnapshot();
    const { getPreparationList, updatePreparationAnnotation } = usePreparation();
    const { refreshReader } = useRefreshReader();
    const { updateDisplayLanguage } = useUpdateDisplayLanguage();
    const updateUserSettings = useUpdateUserSettings();
    const { readAnnotations, readAnnotationById } = useReadAnnotations();
    const { firebaseInitialize } = useFirebase();
    const deeplinkHandledRef = useRef(false); // 避免 effect 因 books/token 變動重跑而重複打 checkValidDomain
    let payload;

    useEffect(() => {
        if (!annotationId && !paramsActivityId && !paramsInteractiveObjectId) {
            const isBrowser = !window.android && !window.ios && !window.electron;
            const isInIframe = (window.parent !== window);
            const hasBypassKey = window.location.search.indexOf('2s8-5c4-ej!$Mg5-4Dkc56-4c54') >= 0;
            const isPlainEntry = !roomId && !studentId && !timeSpanId && !code && !userName && !id && !remote && !remoteId && !paramsRole && !paramsTeacherId;
            const isEditorDomain = window.location.hostname === EDITOR_DOMAIN;
            if (isBrowser && !isEditorDomain && !isInIframe && !hasBypassKey && isPlainEntry && bookId && !bookId.includes('index')) {
                if (deeplinkHandledRef.current) return;
                deeplinkHandledRef.current = true;
                sessionStorage.setItem('deeplinkBookId', bookId);
                sessionStorage.removeItem('deeplinkExternal');
                (async () => {
                    const parentDomain = document.referrer ? document.referrer.split('://')[1].split('/')[0] : '';
                    if (await fetchIsValidDomain(parentDomain)) {
                        sessionStorage.setItem('deeplinkExternal', '1');
                    }
                    router.history.push('/bookshelf');
                })();
                return;
            }
        }

        const book = books.find(book => book.bookId === bookId);
        if (!book) return;
        if (!annotationId && !paramsActivityId && !paramsInteractiveObjectId) {
            EventBus.emit({
                event: UserEvent.GetUserIdentityEvent,
                payload: {
                    roomId,
                    studentId,
                    timeSpanId,
                    code,
                    userName,
                    id,
                    remote,
                    bookId,
                    role: paramsRole,
                    callback: (async ({ role, userId, userName, courseInfo }) => {
                        let type = AnnotationType.GUEST;
                        switch (role) {
                            case Roles.GUEST:
                                type = AnnotationType.GUEST
                                break;
                            case Roles.TEACHER_REMOTE:
                            case Roles.TEACHER:
                            case Roles.CRAM_TEACHER:
                                if (window.android || window.ios || window.electron) {
                                    type = AnnotationType.GUEST
                                } else {
                                    type = AnnotationType.CLASS_PREPARATION
                                }
                                break;
                            case Roles.PARENT:
                            case Roles.STUDENT:
                                type = AnnotationType.CLASS_PREPARATION
                                break;
                            case Roles.EDITOR:
                                type = AnnotationType.INTERACTIVE_OBJECT
                                break;
                            case Roles.TUTOR:
                                type = AnnotationType.ONE_TUTOR
                                break;
                            case Roles.TUTOR_USER:
                                type = AnnotationType.ONE_TUTOR
                                break;
                            case Roles.ONECLASS_TEACHER:
                                type = AnnotationType.ONE_CLASS
                                break;
                            case Roles.ONECLASS_STUDENT:
                                type = AnnotationType.ONE_CLASS
                                break;
                            default:
                                break;
                        }

                        //因為online.mietc.tw 南部工程師用內遷iframe onebook所以要判斷如果是被該網頁內簽的狀態不要擋著使用者使用
                        var isInIframe = (window.parent !== window),
                            parentUrl = '';

                        if (isInIframe) {
                            parentUrl = document.referrer;
                        }

                        const isNanipad = document.referrer.includes('pad.nani.com.tw')
                        const isLive = document.referrer.includes('live') && document.referrer.includes('oneclass.com.tw')
                        const isNewteacher = document.referrer.includes('newteacher') && document.referrer.includes('oneclass.com.tw')
                        const isOneclubBackstage = document.referrer.includes('oneclub.backstage') && document.referrer.includes('oneclass.com.tw')
                        const parentDomain = document.referrer ? document.referrer.split("://")[1].split("/")[0] : ''
                        const isValidDomain = await fetchIsValidDomain(parentDomain);

                        //const isNanipad = window.location.search.includes('test')
                        if (!window.android && !window.ios && !window.electron && !isNanipad && !isValidDomain && !isLive && !isNewteacher && !isOneclubBackstage) {
                            if (role === Roles.GUEST && bookId && parentUrl.indexOf("online.mietc.tw") < 0 && window.location.search.indexOf('2s8-5c4-ej!$Mg5-4Dkc56-4c54') < 0) {
                                alert("需登入南一帳號、教育雲端帳號後才能使用")
                                router.history.push('/bookshelf')
                                return;
                            }
                        }


                        //撈出indexdb裡面的備課資料，取第一筆進書
                        payload = {
                            bookId,
                            annotationType: type,
                            name: "未命名",
                            marks: [],
                            annotationId: userId.includes("remote") ? roomId : null,
                            createNewAnnotation: userId.includes("remote")
                        };

                        if (token) {
                            await getPreparationList(bookId, token, type);
                        }

                        let callback = null;

                        if (type !== AnnotationType.ONE_TUTOR && type !== AnnotationType.ONE_CLASS) {
                            firebaseInitialize()
                            let results = await readAnnotations({ bookId, annotationType: type });
                            if (results.length > 0) {
                                let latestAnnotation = results.filter((annotation) => !annotation.isDeleted).sort((a, b) => {
                                    return b.updatedAt - a.updatedAt;
                                })[0];
                                // deep link 進書：正常書架/書城流程進書前都會 await updatePreparationAnnotation
                                // 把 fabric annotations 逐頁載進 IndexedDB（BookStore / AnnotationModal），
                                // 但 deep link 少了這步，導致 setPageIndex 的孤兒回收在 fabric 尚未載入時，
                                // 以空的 id 清單把 canvasSVGObjects（便利貼內容）誤刪並寫回 DB。
                                // 這裡補上載入，並重讀成含 fabric 的最新資料再進書。
                                if (token) {
                                    await updatePreparationAnnotation({ id: latestAnnotation.id, token });
                                    const refreshedAnnotation = await readAnnotationById({ id: latestAnnotation.id });
                                    if (refreshedAnnotation) latestAnnotation = refreshedAnnotation;
                                }
                                readerDispatch({
                                    type: types.SWITCH_BOOK_PAGES_SHOW,
                                    isDoublePageMode: latestAnnotation.isDoublePageMode,
                                });
                                callback = ({ annotationId }) => {
                                    setTimeout(() => {
                                        if (remoteId) {
                                            roomId && courseDispatch({ type: types.SET_COURSE_INFO, courseInfo: { ...courseInfo, id: roomId, userRole: role, remoteId } })
                                            const isRemote = userId.includes("remote")
                                            isRemote && EventBus.emit({
                                                event: ReaderToolsEvent.SetSyncCameraEvent,
                                                payload: { isOpen: true }
                                            });
                                            isRemote && EventBus.emit({
                                                event: ReaderToolsEvent.SetSyncOnConnected,
                                                payload: { onConnected: true },
                                            });
                                        } else {
                                            courseDispatch({ type: types.SET_COURSE_INFO, courseInfo: { ...courseInfo, id: annotationId, userRole: role, remoteId } })
                                        }
                                    }, 1000)

                                    refreshReader(latestAnnotation)
                                };

                                payload.annotationId = userId.includes("remote") ? roomId : latestAnnotation.id;
                                payload.name = latestAnnotation.name;
                                payload.marks = latestAnnotation.marks;
                                payload.annotations = latestAnnotation.annotations;
                                if (type === AnnotationType.CLASS_PREPARATION) {
                                    //preparationSnapshot(latestAnnotation.id, token)
                                }
                            } else {
                                callback = ({ annotationId }) => {
                                    setTimeout(() => {
                                        roomId && courseDispatch({ type: types.SET_COURSE_INFO, courseInfo: { ...courseInfo, id: roomId, userRole: role, remoteId } })
                                        const isRemote = userId.includes("remote")
                                        isRemote && EventBus.emit({
                                            event: ReaderToolsEvent.SetSyncCameraEvent,
                                            payload: { isOpen: true }
                                        });
                                        isRemote && EventBus.emit({
                                            event: ReaderToolsEvent.SetSyncOnConnected,
                                            payload: { onConnected: true },
                                        });
                                    }, 1000)

                                }

                            }


                        } else {
                            const result = await readAnnotationById({ id: roomId });
                            if (result) {
                                payload.annotationId = result.id;
                                payload.name = result.name;
                                payload.marks = result.marks;
                                payload.annotations = result.annotations;
                            } else {
                                payload.annotationId = roomId;
                                payload.name = userName;
                                payload.createNewAnnotation = true;
                                payload.marks = [];
                                payload.annotations = [];
                            }

                            if (role === Roles.GUEST) {
                                alert("您不是該課程的成員")
                            } else {
                                let enterCourse = false;
                                let isFreeGroup = false;
                                if (courseInfo) {
                                    isFreeGroup = courseInfo.organizationId === undefined ? true : false;
                                    const time = new Date().getTime();
                                    const { startAt, endAt } = courseInfo
                                    if (time >= startAt && time <= endAt) {
                                        enterCourse = true;
                                    }
                                }

                                courseDispatch({ type: types.SET_COURSE_INFO, courseInfo: { ...courseInfo, id: roomId, userRole: role, remoteId } })


                                switch (role) {
                                    case Roles.TUTOR:
                                        firebaseInitialize()
                                        readerDispatch({ type: types.TOGGLE_LIVE_STREAM_MASTER_PANEL, isLiveStreamMasterPanelShow: true });
                                        break;
                                    case Roles.ONECLASS_TEACHER:
                                        if (enterCourse) {
                                            courseDispatch({ type: types.SET_CHIME_ROLE, chimeRole: role })
                                            !isFreeGroup && readerDispatch({ type: types.TOGGLE_LIVE_STREAM_MASTER_PANEL, isLiveStreamMasterPanelShow: true });
                                            firebaseInitialize()
                                        }
                                        break;
                                    case Roles.TUTOR_USER:
                                        firebaseInitialize()
                                        readerDispatch({ type: types.TOGGLE_LIVE_STREAM_VIEWER_PANEL, isLiveStreamViewerPanelShow: true });
                                        break;
                                    case Roles.ONECLASS_STUDENT:
                                        if (enterCourse) {
                                            courseDispatch({ type: types.SET_CHIME_ROLE, chimeRole: role })
                                            !isFreeGroup && readerDispatch({ type: types.TOGGLE_LIVE_STREAM_VIEWER_PANEL, isLiveStreamViewerPanelShow: true });
                                            firebaseInitialize()
                                        }
                                        break;
                                    default:
                                        break;
                                }
                            }
                        }

                        EventBus.emit({
                            event: ReaderEvent.CreateAndEnterAnnotationEvent,
                            payload: {
                                ...payload,
                                callback
                            }
                        });
                    })
                }
            });
        } else {
            if (annotationType === AnnotationType.CLASS_PREPARATION) {
                //preparationSnapshot(annotationId, token)
                refreshReader();
            } else if (annotationType === AnnotationType.GUEST) {
                refreshReader();
            }
        }

        return payload
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [books, token]);
}